import { useGetMe } from 'api-hooks/auth/query';
import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import {
  DetailModel,
  DocumentSaleGuestDetailStatusEnum,
  TravelDocumentSalesModel,
  UploadTravelDocumentSalesMutationInput,
} from 'api-hooks/sale/travel-document';
import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { Option } from 'baseui/select';
import { AuthorizationRules } from 'common/constants';
import { DocumentSaleStatusEnum, EnumList } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import { capitalize } from 'common/utils/string';
import HOCInput from 'components/common/hoc-input';
import notification from 'components/common/notification';
import ParsePassport, {
  PassportType,
} from 'components/common/parse-excel/parse-passport';
import { Form, FormState, Input } from 'components/elements';
import Text from 'components/elements/text';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import TitleStatus from 'legacy-modules/common/title-status';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import useSalesStatus from 'legacy-modules/sales/hooks/use-sales-status';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  OneThirdContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import ApecCardSection from './apec-card-section';
import VisaInsuranceSection from './visa-insurance-section';
import { FormContent, FormDivider } from '../components/styles';

interface Props {
  travelDocument: TravelDocumentSalesModel;
  onSubmit: (
    input: UploadTravelDocumentSalesMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<any | undefined>;
  renderError?: React.ReactNode;
}

function transformer(item: PassportType): Option {
  return {
    id: item.id,
    label: `${item.givenName} - ${item.documentNo}`,
    extra: item,
  };
}

export default function TravelDocumentUploadForm(props: Props) {
  const { travelDocument } = props;
  const { can } = useAuthorization();

  const { t } = useTranslation();

  const [passportOptions, setPassportOptions] = React.useState<Option[]>([]);

  const defaultValues = React.useMemo(
    () => ({
      documentSales: travelDocument.documentSales.map((document) => {
        return {
          id: document?.id,
          completionDateEstimation: document.completionDateEstimation,
          documentType: document.documentType,
          documentSaleGuests: document.documentSaleGuests.map((guest) => {
            return {
              id: guest?.id || '',
              label: guestNameTransformer(guest.guest),
              documentSaleGuestDetails: Array.isArray(guest.details)
                ? (guest.details as DetailModel[]).map((detail) => {
                    return {
                      ...detail,
                      documentStatus: detail.documentStatus || '',
                      number: detail.number || '',
                      issuedAt: detail.issuedAt || null,
                      expiredAt: detail.expiredAt || null,
                      files: detail.files.map((file) => ({
                        id: file?.id,
                        image: file.name,
                        url: file.url,
                      })),
                      id: detail.id || '',
                      preferenceId: detail.visaDocument?.id || '',
                      type: detail.visaDocument?.name || '',
                      // identifierNumber: detail.identifierNumber,
                      // familyName: detail.familyName,
                      // givenName: detail.givenName,
                      // nationality: detail.nasionality,
                      // dateOfBirth: detail.dateOfBirth,
                    };
                  })
                : [
                    {
                      ...guest.details,
                      documentStatus: guest.details.documentStatus || '',
                      number: guest.details.number || '',
                      issueAt: guest.details.issuedAt || null,
                      expiredAt: guest.details.expiredAt || null,
                      files: (guest.details.files || []).map((file) => ({
                        id: file?.id,
                        image: file.name,
                        url: file.url,
                      })),
                      id: guest.details.id || '',
                      preferenceId: guest.details.visaDocument?.id || '',
                      type: capitalize(guest.details.type),
                      identifierNumber: guest.details?.identifierNumber || '',
                      familyName: guest.details?.familyName || '',
                      givenName: guest.details?.givenName || '',
                      nationality: guest.details?.nationality || '',
                      dateOfBirth: guest.details?.dateOfBirth || null,
                      issueState: guest.details?.issueState || '',
                      placeOfBirth: guest.details?.placeOfBirth || '',
                      gender: guest.details?.gender,
                      name: guest.details?.name || '',
                      applicationNumber: guest.details?.applicationNumber || '',
                      passportNumber: guest.details?.passportNumber || '',
                      email: guest.details?.email || '',
                      pin: guest.details?.pin || '',
                      documentNumber: guest.details?.documentNumber || '',
                    },
                  ],
            };
          }),
        };
      }),
    }),
    [travelDocument.documentSales],
  );

  const methods = useForm({
    defaultValues,
    mode: 'all',
  });

  const { setValue } = methods;

  const onSelectOptions = React.useCallback(
    ({
      documentIndex,
      guestIndex,
      detailIndex,
      value,
      detail,
    }: {
      documentIndex: number;
      guestIndex: number;
      detailIndex: number;
      value: PassportType;
      detail: any;
    }) => {
      const _value = {
        ...detail,
        number: value.documentNo ?? '',
        identifierNumber: value.personalNo ?? '',
        familyName: value.familyName ?? '',
        givenName: value.givenName ?? '',
        nationality: value.nationality ?? '',
        gender: value.sex === 'M' ? 'male' : 'female',
        placeOfBirth: value.birthPlace ?? null,
        issueState: value.issueState ?? '',
        expiredAt: value.expireDate ?? null,
        dateOfBirth: value.birthday ?? null,
      };
      setValue(
        `documentSales.${documentIndex}.documentSaleGuests.${guestIndex}.documentSaleGuestDetails.${detailIndex}`,
        _value,
      );
    },
    [setValue],
  );

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const errorNumber = !!(methods.formState.errors as any)?.number;

  React.useEffect(() => {
    if (errorNumber) {
      methods.clearErrors();
    }
  }, [errorNumber, methods]);

  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const isContribute =
    travelDocument?.admin.id === employee?.id ||
    travelDocument?.salesman?.id === employee?.id ||
    travelDocument?.operator?.id === employee?.id;

  const { buttonCondition } = useSalesStatus(travelDocument);

  const isFinished =
    travelDocument?.documentSaleStatus === DocumentSaleStatusEnum.Finished;

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!travelDocument}
      customViewName={
        travelDocument &&
        `${t('common:travel_document_sales')} - ${travelDocument.invoiceNumber}`
      }
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={travelDocument}
            title={
              travelDocument ? (
                <TitleStatus
                  type="sale-document"
                  isCompleted={travelDocument.isCompleted}
                  status={travelDocument.documentSaleStatus}
                  title={`${t('common:travel_document_sales')} - ${
                    travelDocument.invoiceNumber
                  }`}
                />
              ) : (
                t('common:add_extra', {
                  extra: t('common:travel_document_sales'),
                })
              )
            }
            noDelete
            noEdit={
              !can(AuthorizationRules.DocumentSalesUploadDocuments) ||
              !can(AuthorizationRules.SalesCore) ||
              !isContribute ||
              (!buttonCondition.edit && isFinished)
            }
          />
        )}
      </FormState>
      {props.renderError}
      <FormContent>
        <div>
          <HOCInput keys={['documentSales']}>
            {(value) =>
              (value.documentSales || []).map((document, documentIndex) => (
                <React.Fragment key={document.id || document.customId}>
                  {document.completionDateEstimation && (
                    <HalfContainer>
                      <Input
                        label={t('common:completion_date_estimation')}
                        type="date-time"
                        name={`documentSales.${documentIndex}.completionDateEstimation`}
                        required
                      />
                    </HalfContainer>
                  )}

                  {document.documentType === DocumentTypeEnum.passport && (
                    <FormState>
                      {({ editable }) =>
                        editable ? (
                          <>
                            <SectionContainer>
                              <ParsePassport
                                text={
                                  passportOptions.length
                                    ? t('common:reupload')
                                    : undefined
                                }
                                onResult={(result: PassportType[]) => {
                                  const _options = result?.map((item) =>
                                    transformer(item),
                                  );
                                  setPassportOptions(_options);
                                }}
                                type="passport"
                              />
                            </SectionContainer>
                            <SeparatorVertical $margin={24} />
                          </>
                        ) : (
                          <></>
                        )
                      }
                    </FormState>
                  )}
                  {document.documentSaleGuests.map((guest, guestIndex) => (
                    <FullContainer key={guest.id}>
                      <Text variant="HeadingXSmall">{guest.label}</Text>
                      <SeparatorVertical $margin={24} />
                      {guest.documentSaleGuestDetails.map(
                        (detail, detailIndex) => {
                          const parentName = `documentSales.${documentIndex}.documentSaleGuests.${guestIndex}.documentSaleGuestDetails.${detailIndex}`;

                          return document.documentType ===
                            DocumentTypeEnum.passport ? (
                            <>
                              {!!passportOptions.length && (
                                <HalfContainer>
                                  <Input
                                    type="select"
                                    name={`${parentName}.passport`}
                                    options={passportOptions}
                                    onAfterChange={(params) => {
                                      if (params.value?.[0]?.extra) {
                                        const _selectedValue = params.value[0]
                                          .extra as PassportType;
                                        onSelectOptions({
                                          guestIndex,
                                          detailIndex,
                                          documentIndex,
                                          value: _selectedValue,
                                          detail,
                                        });
                                      }
                                    }}
                                  />
                                </HalfContainer>
                              )}
                              <CardSection style={{ paddingBottom: 0 }}>
                                <FullContainer>
                                  <SectionContainer>
                                    <OneThirdContainer>
                                      <Input
                                        type="text"
                                        name={`${parentName}.type`}
                                        label={t('common:type')}
                                        placeholder={t('common:type')}
                                        disabled
                                      />
                                    </OneThirdContainer>
                                    <OneThirdContainer>
                                      <EnumSelectInput
                                        enumType={
                                          EnumList.DocumentSaleGuestDetailStatus
                                        }
                                        name={`${parentName}.documentStatus`}
                                        label={t('document:document_status')}
                                        placeholder={t(
                                          'document:document_status',
                                        )}
                                      />
                                    </OneThirdContainer>
                                    <OneThirdContainer>
                                      {detail.documentStatus !==
                                        DocumentSaleGuestDetailStatusEnum.Rejected && (
                                        <Input
                                          type="text"
                                          name={`${parentName}.number`}
                                          label={t('common:document_number')}
                                          placeholder={t(
                                            'common:document_number',
                                          )}
                                        />
                                      )}
                                    </OneThirdContainer>
                                  </SectionContainer>
                                  {detail.documentStatus !==
                                    DocumentSaleGuestDetailStatusEnum.Rejected && (
                                    <>
                                      <SectionContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="text"
                                            name={`${parentName}.identifierNumber`}
                                            label={t('common:ktp')}
                                            placeholder={t('common:ktp')}
                                          />
                                        </OneThirdContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="text"
                                            name={`${parentName}.familyName`}
                                            label={t('common:family_name')}
                                            placeholder={t(
                                              'common:family_name',
                                            )}
                                          />
                                        </OneThirdContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="text"
                                            name={`${parentName}.givenName`}
                                            label={t('common:given_name')}
                                            placeholder={t('common:given_name')}
                                          />
                                        </OneThirdContainer>
                                      </SectionContainer>
                                      <SectionContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="text"
                                            name={`${parentName}.nationality`}
                                            label={t('common:nationality')}
                                            placeholder={t(
                                              'common:nationality',
                                            )}
                                          />
                                        </OneThirdContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="radio-group"
                                            name={`${parentName}.gender`}
                                            options={[
                                              {
                                                value: 'male',
                                                children: t('common:male'),
                                              },
                                              {
                                                value: 'female',
                                                children: t('common:female'),
                                              },
                                            ]}
                                            label={t('common:gender')}
                                            align="horizontal"
                                          />
                                        </OneThirdContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="text"
                                            name={`${parentName}.placeOfBirth`}
                                            label={t('common:place_of_birth')}
                                            placeholder={t(
                                              'common:place_of_birth',
                                            )}
                                          />
                                        </OneThirdContainer>
                                      </SectionContainer>
                                      <SectionContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="date-time"
                                            name={`${parentName}.dateOfBirth`}
                                            label={t('common:date_of_birth')}
                                            placeholder={t(
                                              'common:date_of_birth',
                                            )}
                                          />
                                        </OneThirdContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="text"
                                            name={`${parentName}.issueState`}
                                            label={t('common:issue_state')}
                                            placeholder={t(
                                              'common:issue_state',
                                            )}
                                          />
                                        </OneThirdContainer>
                                        <OneThirdContainer>
                                          <Input
                                            type="date-time"
                                            name={`${parentName}.expiredAt`}
                                            label={t('common:date_of_expiry')}
                                            placeholder={t(
                                              'common:date_of_expiry',
                                            )}
                                          />
                                        </OneThirdContainer>
                                      </SectionContainer>
                                      <OneThirdContainer>
                                        <Input
                                          type="files"
                                          name={`${parentName}.files`}
                                          label={t('common:upload_document')}
                                          valueKey="image"
                                          previewKey="url"
                                          fileType={
                                            UploadFileTypeEnum.DocumentSalePassportGuestDetail
                                          }
                                        />
                                      </OneThirdContainer>
                                    </>
                                  )}
                                </FullContainer>
                              </CardSection>
                              <SeparatorVertical $margin={36} />
                            </>
                          ) : document.documentType ===
                            DocumentTypeEnum.apec_card ? (
                            <ApecCardSection
                              documentIndex={documentIndex}
                              parentName={parentName}
                              documentStatus={detail.documentStatus}
                            />
                          ) : (
                            <VisaInsuranceSection
                              documentIndex={documentIndex}
                              parentName={parentName}
                              type={document.documentType}
                              documentStatus={detail.documentStatus}
                            />
                          );
                        },
                      )}
                    </FullContainer>
                  ))}
                </React.Fragment>
              ))
            }
          </HOCInput>
          <FormDivider />
        </div>
      </FormContent>
    </Form>
  );
}
